import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationTriangle, FaArrowUp } from 'react-icons/fa';
import {
  FIELD_ORDER_BY_STEP,
  FIELD_LABEL_KEYS,
  scrollToAndFocusField,
} from './collabValidationHelper';
import './FormErrorSummary.scss';

const FormErrorSummary = ({
  errors = {},
  currentStep = 1,
  prefix = 'offer',
  onFieldClick,
  t,
}) => {
  // Collect all fields that currently have an error message
  const activeFieldsWithErrors = Object.keys(errors).filter(
    (key) => Boolean(errors[key])
  );

  if (activeFieldsWithErrors.length === 0) return null;

  // Prioritize fields belonging to currentStep in visual order
  const stepOrder = FIELD_ORDER_BY_STEP[currentStep] || [];
  const orderedStepErrors = stepOrder.filter((field) =>
    Boolean(errors[field])
  );

  // If there are other errors outside currentStep (e.g. backend validation)
  const remainingErrors = activeFieldsWithErrors.filter(
    (f) => !stepOrder.includes(f)
  );

  const displayFields = [...orderedStepErrors, ...remainingErrors];
  if (displayFields.length === 0) return null;

  const handleClick = (fieldKey) => {
    if (typeof onFieldClick === 'function') {
      onFieldClick(fieldKey);
    } else {
      scrollToAndFocusField(fieldKey, prefix);
    }
  };

  const translateLabel = (key) => {
    const labelKey = FIELD_LABEL_KEYS[key];
    if (labelKey && typeof t === 'function') {
      const translated = t(labelKey);
      if (translated && translated !== labelKey) return translated;
    }
    // Human readable fallback
    return key.replace(/_/g, ' ');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className="v2-form-error-summary"
        role="alert"
        aria-live="polite"
      >
        <div className="v2-summary-header">
          <div className="v2-summary-icon" aria-hidden="true">
            <FaExclamationTriangle />
          </div>
          <div className="v2-summary-text">
            <h4 className="v2-summary-title">
              {typeof t === 'function'
                ? t('create.errors.summary_title', 'Revisa los campos requeridos para continuar')
                : 'Revisa los campos requeridos para continuar'}
            </h4>
            <p className="v2-summary-subtitle">
              {displayFields.length === 1
                ? (typeof t === 'function'
                    ? t('create.errors.summary_one', 'Hay 1 campo que requiere tu atención. Haz clic para ir directo:')
                    : 'Hay 1 campo que requiere tu atención. Haz clic para ir directo:')
                : (typeof t === 'function'
                    ? t('create.errors.summary_multiple', {
                        count: displayFields.length,
                        defaultValue: `Hay ${displayFields.length} campos que requieren tu atención. Haz clic para corregir:`,
                      })
                    : `Hay ${displayFields.length} campos que requieren tu atención. Haz clic para corregir:`)}
            </p>
          </div>
        </div>

        <div className="v2-summary-chips">
          {displayFields.map((fieldKey) => (
            <button
              key={fieldKey}
              type="button"
              className="v2-error-chip"
              onClick={() => handleClick(fieldKey)}
              title={errors[fieldKey]}
            >
              <FaArrowUp aria-hidden="true" />
              <span>{translateLabel(fieldKey)}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FormErrorSummary;
